import { Injectable } from '@angular/core';

import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

import { AlertDialogAngularComponent } from './AlertDialogAngular.Component';
import { ConfirmationDialogAngularComponent } from './ConfirmationDialogAngular.Component';
import { IConfirmationDialogParams } from './IConfirmationDialogParams';

export interface IDialogServiceOptions extends ModalOptions<any> {
	component?: any;
	resolve?: any;
	modalService?: BsModalService; //Only Needed if modal is lazy-loaded and accessed from angularJs
	ariaLabelledBy?: string;
}

export interface IModalRef {
	open(options?: IDialogServiceOptions): BsModalRef;
}

const baseModalCfg: IDialogServiceOptions = {
	keyboard: true,
	ignoreBackdropClick: false,
	ariaLabelledBy: 'modal-title'
};

@Injectable({
	providedIn: 'root'
})
export class DialogService {
	public static configs: { [key: string]: IDialogServiceOptions; } = {};

	constructor(
		private readonly modalService: BsModalService
	) { }

	//Not many dialogs have a registration config. Calling register is optional in those cases.
	//use openDialogByClass instead
	public static register(dialogName: string, config: IDialogServiceOptions): void {
		if (DialogService.configs[dialogName]) {
			return;
		}

		DialogService.configs[dialogName] = config;
	}

	public openDialogByClass<T>(componentType: new (...args: any[]) => T, config?: any): BsModalRef<T> {
		const modalService: BsModalService = config?.modalService || this.modalService;

		return modalService.show(componentType, {
			...baseModalCfg,
			...config
		});
	}

	public getDialog<T>(name: string): IModalRef {
		const config: any = DialogService.configs[name];

		if (!config) {
			throw new Error('Unknown Dialog Type: ' + name);
		}

		return {
			open: (options: IDialogServiceOptions = {}): BsModalRef<T> => {
				return this.openDialogByClass(config.component, {
					...baseModalCfg,
					...config,
					...options
				});
			}
		};
	}

	public openConfirmationDialog(params: IConfirmationDialogParams): ConfirmationDialogAngularComponent {
		return this.getDialog<ConfirmationDialogAngularComponent>('ConfirmationDialog')
			.open({
				ariaDescribedby: 'modal-body',
				initialState: {
					params
				}
			})
			.content;
	}

	public openAlertDialog(title: string, message: string, okButtonText: string): AlertDialogAngularComponent {
		return this.getDialog<AlertDialogAngularComponent>('AlertDialog')
			.open({
				initialState: {
					title,
					message,
					okButtonText
				}
			})
			.content;
	}

	public openStorysetDialog(title: string, message: string, okButtonText: string, imageUrl?: string): AlertDialogAngularComponent {
		return this.getDialog<AlertDialogAngularComponent>('StorysetDialog')
			.open({
				initialState: {
					title,
					message,
					okButtonText,
					imageUrl
				}
			})
			.content;
	}
}
